import { Component, Input } from '@angular/core';

/**
 * Truncating text cell for `app-table` — same extension mechanism as
 * `AlTableBadgeComponent` / `StatusIconComponent`.
 *
 * Long free text (a rule description, a note) otherwise stretches the row and
 * crowds every neighbouring column.
 *
 * Two mechanisms, in this order:
 *   1. CSS does the visible cut — single-line ellipsis at whatever width the
 *      column actually has. That is the elegant one, and it adapts.
 *   2. `max` is a generous character guard on top, so a pathological value
 *      (a pasted paragraph) never becomes a giant tooltip either.
 *
 * The full string is always reachable on hover, so nothing is lost.
 *
 *   {
 *     key: 'definition',
 *     component: AlTableTextComponent,
 *     componentInputs: ({ value }) => ({ text: value, max: 70 }),
 *   }
 */
@Component({
  selector: 'app-table-text',
  templateUrl: './table-text.component.html',
  styleUrl: './table-text.component.scss',
})
export class AlTableTextComponent {
  @Input() text = '';
  @Input() max = 160;
  /** Rendered when `text` is empty. */
  @Input() placeholder = '—';
  /** Optional small suffix badge, e.g. an origin marker. */
  @Input() suffix?: string;
  @Input() suffixTooltip?: string;

  get clean(): string {
    return (this.text || '').trim();
  }

  get truncated(): boolean {
    return this.clean.length > this.max;
  }

  get display(): string {
    if (!this.clean) return this.placeholder;
    return this.truncated
      ? this.clean.slice(0, this.max - 1).trimEnd() + '…'
      : this.clean;
  }
}
